// hooks/useValidateCurrencyRedirect.ts
"use client";

import { usePathname, useRouter } from "next/navigation";

import {
  buildLocalePath,
  getCountryCode,
  getCurrencyFromLocale,
  getDefaultLocaleForCountry,
  localeCurrencyMap,
  normalizeCode,
  parseLocalePath,
  toCanonical,
} from "@repo/locale";
import { resetApp, resetToMain } from "@repo/store";

import { bookingFlowPages } from "@repo/ui/constants/routesList";

import { useAppDispatch, useAppSelector } from "@repo/store/hooks";
import { persistor } from "@repo/store/persistor";
import { selectIsCheckIn, setCurrency, setLocale, useIsPostBooking } from "@repo/store/slices";

import { CURRENT_ZONE, HOME_URL, homeUrl, zoneRoutePath } from "../constants/bookingBase";

type ValidateCurrencyRedirectArgs = {
  locale: string;
  // currency: string;
  oldCurrency: string;
};

export function useValidateCurrencyRedirect() {
  const router = useRouter();
  const pathname = usePathname();
  const dispatch = useAppDispatch();
  const isCheckin = useAppSelector(selectIsCheckIn);
  const isPostBooking = useIsPostBooking();
  const flightUrl = "/flight";
  const lastSegment = "/" + pathname.split("/").filter(Boolean).pop();

  const isBookingFlowPage = !!lastSegment && bookingFlowPages.has(lastSegment);

  const validate = ({ locale, oldCurrency }: ValidateCurrencyRedirectArgs) => {
    // A country-picker localeCode can carry a wrong language part from the CMS
    // (the English/US option arrives as "es_us"). Resolve to a real LanguageCode,
    // falling back to the selected country's default locale (es_us → US → en_us).
    const resolvedCode =
      normalizeCode(locale) ?? getDefaultLocaleForCountry(getCountryCode(locale));
    const effectiveLocale = resolvedCode ?? locale;

    const formatedLocale = toCanonical(effectiveLocale);
    const defaultCurrencyForLocale = getCurrencyFromLocale(effectiveLocale);
    dispatch(setLocale(formatedLocale));
    dispatch(setCurrency(defaultCurrencyForLocale));

    const supportedCurrencies = localeCurrencyMap[formatedLocale] ?? [];

    // Culture change must update the URL to the NEW /{country}/{lang} prefix.
    // We build the target prefix explicitly from the new code (this navigates to
    // a DIFFERENT locale than the current URL, so it can't use useLocalizedRouter,
    // which prefixes with the CURRENT locale).
    const newCode = resolvedCode;
    const { code: activeCode, rest } = parseLocalePath(pathname);

    // The bare home ("/", i.e. `/fly`) has no locale segment, yet it is main's migrated
    // home that the middleware normally 307s to /{country}/{lang}. Refreshing it would make
    // the middleware re-resolve the locale from the `locale` cookie, which still holds the
    // OLD locale (the session-preferences action never writes it), so the whole page
    // re-rendered in the previous language. Navigate to the NEW prefix instead.
    if (!activeCode && newCode && pathname === "/") {
      router.push(buildLocalePath(newCode));
      return;
    }

    // Phased-rollout safety: only rewrite the URL locale when the current URL
    // already carries one (a migrated app). Otherwise keep legacy behavior so
    // not-yet-migrated apps are unaffected.
    if (activeCode && newCode) {
      const newPrefix = buildLocalePath(newCode);
      if (!isBookingFlowPage || isCheckin || isPostBooking) {
        router.push(`${newPrefix}${rest}`);
        return;
      }
      if (supportedCurrencies.includes(oldCurrency)) {
        dispatch(resetApp());
        // `flightUrl` is a BARE, zone-relative literal ("/flight"), and
        // `withLocalePrefix` only attaches the locale — so in a zone app it produced
        // `/cl/es/flight`, which Next turned into `/fly/cl/es/flight`: a 404, because the
        // page lives at `/fly/cl/es/{zone}/flight`. Reported from the deployed env after
        // changing country on the flight page. `zoneRoutePath` re-attaches the zone and
        // stays basePath-relative, which is what `router.push` needs (Next adds APP_BASE).
        // It must take the NEW code explicitly — `useLocalizedRouter`/`localizeHref` would
        // prefix the locale we are navigating AWAY from.
        router.push(zoneRoutePath(CURRENT_ZONE, flightUrl, newCode)); // currency available in locale
      } else {
        // dev-next: currency not available for the new locale → send the user home
        // to start a fresh search, clearing the in-progress booking/search first so
        // the stale search does not rehydrate and pre-fill the home form.
        // Localized variant: target the NEW locale's home.
        dispatch(resetToMain());
        if (typeof window !== "undefined") {
          // Cross-zone hard reload: flush the cleared state to storage before
          // navigating so redux-persist does not rehydrate the stale search.
          // Browser-level navigation, so the target MUST carry APP_BASE — nothing
          // else adds it here (unlike router.push/redirect, where Next does).
          const homeHref = homeUrl("", newCode);
          void persistor.flush().finally(() => {
            window.location.href = homeHref;
          });
        } else {
          // router.push: basePath is added by Next, so stay basePath-relative
          router.push(buildLocalePath(newCode));
        }
      }
      return;
    }

    // Legacy behavior (no URL locale segment present).
    if (!isBookingFlowPage || isCheckin || isPostBooking) {
      if (typeof window !== "undefined" && !window.matchMedia("(hover: hover)").matches) {
        window.location.reload();
      } else {
        router.refresh();
      }
      return;
    }

    if (supportedCurrencies.includes(oldCurrency)) {
      dispatch(resetApp());
      router.push(flightUrl); // currency available in locale
    } else {
      // Currency not available for the new locale → send the user home to start a
      // fresh search. Clear the in-progress booking/search first so the stale
      // search (resolved for the previous culture) does not rehydrate and pre-fill
      // the home form with values that silently fail submit validation.
      dispatch(resetToMain());
      if (typeof window !== "undefined") {
        // Cross-zone hard reload: flush the cleared state to storage before
        // navigating so redux-persist does not rehydrate the stale search.
        void persistor.flush().finally(() => {
          window.location.href = HOME_URL;
        });
      } else {
        router.push(HOME_URL); // currency not available for locale → redirect home
      }
    }
  };

  return { validate };
}
